import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Save } from 'lucide-react';
import { Button, Card, CardContent } from '@mui/material';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import { PageHeader } from '../components/ui';
import './OfficeForm.css';

const EMPTY_OFFICE = {
  city: '',
  officeName: '',
  address: '',
  phonePrimary: '',
  phoneSecondary: '',
  email: '',
  timings: '',
  mapLink: '',
  order: 0,
  isActive: true
};

const OfficeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = id && id !== 'new';

  const [form, setForm] = useState(EMPTY_OFFICE);
  const { showToast } = useToast();

  const { data: officesData, isLoading: officesLoading } = useQuery(
    'contact-offices',
    adminAPI.getContactOffices,
    { enabled: isEdit }
  );
  const offices = officesData?.data?.data || [];
  const office = isEdit ? offices.find((o) => o.id === id) : null;

  useEffect(() => {
    if (isEdit && office) {
      setForm({
        city: office.city || '',
        officeName: office.officeName || '',
        address: office.address || '',
        phonePrimary: office.phonePrimary || '',
        phoneSecondary: office.phoneSecondary || '',
        email: office.email || '',
        timings: office.timings || '',
        mapLink: office.mapLink || '',
        order: office.order ?? 0,
        isActive: office.isActive !== false
      });
    }
    if (!isEdit) {
      setForm(EMPTY_OFFICE);
    }
  }, [isEdit, office]);

  const saveMutation = useMutation(
    (payload) =>
      isEdit ? adminAPI.updateContactOffice(id, payload) : adminAPI.createContactOffice(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contact-offices');
        navigate(-1);
        showToast({
          message: isEdit ? 'Office updated successfully.' : 'Office created successfully.',
          severity: 'success'
        });
      },
      onError: (err) =>
        showToast({
          message: err?.response?.data?.message || 'Failed to save office',
          severity: 'error'
        })
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.city.trim() || !form.address.trim()) return;
    saveMutation.mutate({ ...form, order: Number(form.order || 0) });
  };

  if (isEdit && offices.length > 0 && !office && !officesLoading) {
    return (
      <div className="office-form-page">
        <p>Office not found.</p>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
          Back to Get In Touch
        </button>
      </div>
    );
  }

  if (isEdit && officesLoading) {
    return (
      <div className="office-form-page">
        <p>Loading office...</p>
      </div>
    );
  }

  return (
    <div className="office-form-page module-page">
      <AdminBackLink to="/contact" label="Back to Get In Touch" />
      <PageHeader
        title={isEdit ? 'Edit Office' : 'Add Office'}
        subtitle={isEdit ? 'Update office details below.' : 'Add a new contact office.'}
      />

      <Card component="form" onSubmit={handleSubmit} className="office-form-card">
        <CardContent>
        <div className="office-form-grid">
          <div className="office-form-field">
            <label>City *</label>
            <input
              className="input"
              placeholder="City *"
              value={form.city}
              onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
              required
            />
          </div>
          <div className="office-form-field">
            <label>Office name</label>
            <input
              className="input"
              placeholder="Office name"
              value={form.officeName}
              onChange={(e) => setForm((p) => ({ ...p, officeName: e.target.value }))}
            />
          </div>
          <div className="office-form-field office-form-field-full">
            <label>Address *</label>
            <textarea
              className="input"
              placeholder="Address *"
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              rows={3}
              required
            />
          </div>
          <div className="office-form-field">
            <label>Primary phone</label>
            <input
              className="input"
              placeholder="Primary phone"
              value={form.phonePrimary}
              onChange={(e) => setForm((p) => ({ ...p, phonePrimary: e.target.value }))}
            />
          </div>
          <div className="office-form-field">
            <label>Secondary phone</label>
            <input
              className="input"
              placeholder="Secondary phone"
              value={form.phoneSecondary}
              onChange={(e) => setForm((p) => ({ ...p, phoneSecondary: e.target.value }))}
            />
          </div>
          <div className="office-form-field">
            <label>Office email</label>
            <input
              className="input"
              type="email"
              placeholder="Office email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            />
          </div>
          <div className="office-form-field">
            <label>Timings</label>
            <input
              className="input"
              placeholder="Timings"
              value={form.timings}
              onChange={(e) => setForm((p) => ({ ...p, timings: e.target.value }))}
            />
          </div>
          <div className="office-form-field">
            <label>Map link URL</label>
            <input
              className="input"
              placeholder="Map link URL"
              value={form.mapLink}
              onChange={(e) => setForm((p) => ({ ...p, mapLink: e.target.value }))}
            />
          </div>
          <div className="office-form-field">
            <label>Display order</label>
            <input
              className="input"
              type="number"
              placeholder="Display order"
              value={form.order}
              onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
            />
          </div>
          <div className="office-form-field">
            <label>Active</label>
            <select
              className="input"
              value={String(form.isActive)}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
        <div className="office-form-actions">
          <Button
            type="submit"
            variant="contained"
            disabled={saveMutation.isLoading || !form.city.trim() || !form.address.trim()}
            startIcon={<Save size={16} />}
          >
            {saveMutation.isLoading ? 'Saving...' : isEdit ? 'Update Office' : 'Add Office'}
          </Button>
          <Button type="button" variant="outlined" onClick={() => navigate('/contact')}>
            Cancel
          </Button>
        </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OfficeForm;
